// Vendor Leads — Satış pipeline (Kanban) + form
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, EmptyState, ListPageHeader } from '../components/ui';

const fmt = (n) => new Intl.NumberFormat('tr-TR', { style:'currency', currency:'TRY', minimumFractionDigits: 0 }).format(Number(n) || 0);
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('tr-TR') : '—';

const STAGES = [
  { code: 'new',         label: '🆕 Yeni',       clr: '#64748b' },
  { code: 'contacted',   label: '📞 İletişimde', clr: '#2563eb' },
  { code: 'qualified',   label: '✅ Nitelikli',   clr: '#0ea5e9' },
  { code: 'demo',        label: '📊 Demo',       clr: '#8b5cf6' },
  { code: 'negotiating', label: '💼 Pazarlık',    clr: '#d97706' },
  { code: 'won',         label: '🎉 Kazanıldı',   clr: '#059669' },
  { code: 'lost',        label: '❌ Kaybedildi',  clr: '#dc2626' },
];

export default function VendorLeadsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban'); // 'kanban' | 'list'
  const [createOpen, setCreateOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/vendor/leads', { params: { limit: 500 } });
      setList(r.data.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const moveStage = async (lead, newStatus) => {
    try {
      await api.put('/vendor/leads/' + lead.id, { status: newStatus });
      load();
    } catch (err) { alert('Hata'); }
  };

  const totalMRR = list.filter(l => !['won','lost'].includes(l.status)).reduce((s, l) => s + (parseFloat(l.expected_mrr) || 0), 0);
  const wonMRR  = list.filter(l => l.status === 'won').reduce((s, l) => s + (parseFloat(l.expected_mrr) || 0), 0);

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Lead'ler & Satış Pipeline"
        unit="lead"
        count={list.length}
        actions={
          <div style={{ display:'flex', gap: 6 }}>
            <Button size="sm" variant={view === 'kanban' ? 'primary' : 'ghost'} onClick={() => setView('kanban')}>📊 Kanban</Button>
            <Button size="sm" variant={view === 'list' ? 'primary' : 'ghost'} onClick={() => setView('list')}>📋 Liste</Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>+ Yeni Lead</Button>
          </div>
        }
        onRefresh={load} refreshing={loading}
      />

      {/* KPI */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
                    gap: 10, padding:'0 12px 12px' }}>
        <Kpi label="Toplam Lead"       value={list.length} />
        <Kpi label="Pipeline'da MRR"   value={fmt(totalMRR)} clr="var(--accent-primary)" />
        <Kpi label="Kazanılan MRR"     value={fmt(wonMRR)}   clr="var(--success)" />
        <Kpi label="Won Conversion"    value={list.length > 0 ? `%${Math.round((list.filter(l => l.status === 'won').length * 100) / list.length)}` : '—'}
             clr="var(--success)" />
      </div>

      {view === 'kanban' ? (
        <div style={{ display:'flex', gap: 10, padding:'0 12px 20px', overflowX:'auto', alignItems:'flex-start' }}>
          {STAGES.map(s => {
            const items = list.filter(l => l.status === s.code);
            return (
              <div key={s.code} style={{ minWidth: 280, width: 280, flex:'0 0 280px',
                                          background:'var(--bg-secondary)', border:'1px solid var(--border-secondary)',
                                          borderRadius: 8, borderTop:'3px solid ' + s.clr }}>
                <div style={{ padding:'10px 12px', borderBottom:'1px solid var(--border-secondary)',
                              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontWeight:600, fontSize:'0.85rem' }}>{s.label}</span>
                  <span style={{ fontSize:'0.75rem', padding:'1px 8px', borderRadius:100, background: s.clr + '20', color: s.clr, fontWeight:700 }}>{items.length}</span>
                </div>
                <div style={{ padding: 8, display:'flex', flexDirection:'column', gap: 8, maxHeight: '60vh', overflowY:'auto' }}>
                  {items.map(l => (
                    <div key={l.id} onClick={() => setEditLead(l)}
                         style={{ padding:'10px 12px', background:'var(--bg-primary)', border:'1px solid var(--border-secondary)',
                                  borderRadius: 6, cursor:'pointer' }}>
                      <div style={{ fontWeight:600, fontSize:'0.85rem' }}>{l.company_name}</div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)', marginTop: 2 }}>
                        {l.contact_name && <span>{l.contact_name} · </span>}
                        {l.email}
                      </div>
                      {l.expected_mrr && (
                        <div style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--success)', marginTop: 4, fontVariantNumeric:'tabular-nums' }}>
                          {fmt(l.expected_mrr)}/ay
                        </div>
                      )}
                      {l.next_action && (
                        <div style={{ fontSize:'0.7rem', color:'var(--accent-primary)', marginTop: 4 }}>→ {l.next_action}</div>
                      )}
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div style={{ padding: 14, textAlign:'center', color:'var(--text-tertiary)', fontSize:'0.78rem' }}>Boş</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="stocks-table-wrap">
          {loading ? <div>Yükleniyor...</div>
           : list.length === 0 ? <EmptyState title="Henüz lead yok" />
           : (
            <table className="stocks-table">
              <thead>
                <tr>
                  <th>Şirket</th><th>İletişim</th><th>Sektör</th>
                  <th>Aşama</th><th>Tahmini MRR</th><th>Sonraki Aksiyon</th>
                  <th style={{ width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {list.map(l => {
                  const stage = STAGES.find(s => s.code === l.status);
                  return (
                    <tr key={l.id} onClick={() => setEditLead(l)} style={{ cursor:'pointer' }}>
                      <td><strong>{l.company_name}</strong></td>
                      <td>{l.contact_name}<div style={{ fontSize:'0.72rem', color:'var(--text-tertiary)' }}>{l.email}</div></td>
                      <td>{l.industry || '—'}</td>
                      <td><span style={{ fontSize:'0.72rem', padding:'2px 8px', borderRadius: 100, fontWeight: 600,
                                          background: stage?.clr + '20', color: stage?.clr }}>{stage?.label}</span></td>
                      <td>{l.expected_mrr ? fmt(l.expected_mrr) : '—'}</td>
                      <td style={{ fontSize:'0.78rem' }}>{l.next_action || '—'}</td>
                      <td>
                        <select value={l.status} onClick={e => e.stopPropagation()}
                                onChange={e => moveStage(l, e.target.value)}
                                style={{ fontSize:'0.78rem', padding:'2px 6px' }}>
                          {STAGES.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {(createOpen || editLead) && (
        <LeadModal lead={editLead} onClose={() => { setCreateOpen(false); setEditLead(null); }}
                   onSaved={() => { setCreateOpen(false); setEditLead(null); load(); }} />
      )}
    </div>
  );
}

function Kpi({ label, value, clr }) {
  return (
    <div style={{ background:'var(--bg-secondary)', border:'1px solid var(--border-secondary)', borderRadius: 8,
                  padding:'12px 14px', borderTop:'3px solid ' + (clr || 'var(--border-secondary)') }}>
      <div style={{ fontSize:'0.68rem', color:'var(--text-tertiary)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize:'1.3rem', fontWeight:700, color: clr || 'var(--text-primary)', fontVariantNumeric:'tabular-nums' }}>{value}</div>
    </div>
  );
}

function LeadModal({ lead, onClose, onSaved }) {
  const editing = !!lead;
  const [form, setForm] = useState({
    company_name: lead?.company_name || '',
    contact_name: lead?.contact_name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    industry: lead?.industry || '',
    company_size: lead?.company_size || '',
    message: lead?.message || '',
    source: lead?.source || 'manual',
    status: lead?.status || 'new',
    expected_mrr: lead?.expected_mrr || '',
    next_action: lead?.next_action || '',
    next_action_at: lead?.next_action_at ? new Date(lead.next_action_at).toISOString().substring(0, 10) : '',
    lost_reason: lead?.lost_reason || '',
  });
  const [busy, setBusy] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.company_name || !form.email) return alert('Şirket adı ve e-posta gerekli');
    setBusy(true);
    try {
      if (editing) await api.put('/vendor/leads/' + lead.id, form);
      else await api.post('/vendor/leads', form);
      onSaved();
    } catch (err) { alert(err.response?.data?.message || 'Hata'); setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm('Lead silinsin mi?')) return;
    await api.delete('/vendor/leads/' + lead.id);
    onSaved();
  };

  return (
    <Modal title={editing ? 'Lead Düzenle' : 'Yeni Lead'} onClose={onClose} size="lg">
      <div style={{ padding: 18, display:'flex', flexDirection:'column', gap: 12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <FormField label="Şirket Adı" required><input className="ui-input" value={form.company_name} onChange={e => f('company_name', e.target.value)} /></FormField>
          <FormField label="İletişim Kişisi"><input className="ui-input" value={form.contact_name} onChange={e => f('contact_name', e.target.value)} /></FormField>
          <FormField label="E-posta" required><input className="ui-input" type="email" value={form.email} onChange={e => f('email', e.target.value)} /></FormField>
          <FormField label="Telefon"><input className="ui-input" value={form.phone} onChange={e => f('phone', e.target.value)} /></FormField>
          <FormField label="Sektör"><input className="ui-input" value={form.industry} onChange={e => f('industry', e.target.value)} placeholder="İnşaat, Tekstil, Pergola..." /></FormField>
          <FormField label="Şirket Büyüklüğü">
            <select className="ui-input" value={form.company_size} onChange={e => f('company_size', e.target.value)}>
              <option value="">—</option>
              <option value="1-5">1-5 çalışan</option>
              <option value="6-20">6-20 çalışan</option>
              <option value="21-50">21-50 çalışan</option>
              <option value="50+">50+ çalışan</option>
            </select>
          </FormField>
          <FormField label="Aşama">
            <select className="ui-input" value={form.status} onChange={e => f('status', e.target.value)}>
              {STAGES.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          </FormField>
          <FormField label="Tahmini MRR"><input className="ui-input" type="number" value={form.expected_mrr} onChange={e => f('expected_mrr', e.target.value)} placeholder="₺/ay" /></FormField>
          <FormField label="Kaynak">
            <select className="ui-input" value={form.source} onChange={e => f('source', e.target.value)}>
              <option value="manual">Manuel</option>
              <option value="website">Web sitesi</option>
              <option value="referral">Referans</option>
              <option value="ads">Reklam</option>
              <option value="partner">Partner</option>
              <option value="cold">Soğuk arama</option>
            </select>
          </FormField>
        </div>

        <FormField label="Sonraki Aksiyon"><input className="ui-input" value={form.next_action} onChange={e => f('next_action', e.target.value)} placeholder="Örn: Pazartesi demo yap" /></FormField>
        <FormField label="Aksiyon Tarihi"><input className="ui-input" type="date" value={form.next_action_at} onChange={e => f('next_action_at', e.target.value)} /></FormField>

        <FormField label="Müşteri Mesajı / Notlar">
          <textarea className="ui-input" value={form.message} onChange={e => f('message', e.target.value)} rows={3} />
        </FormField>

        {form.status === 'lost' && (
          <FormField label="Kayıp Sebebi"><input className="ui-input" value={form.lost_reason} onChange={e => f('lost_reason', e.target.value)} placeholder="Fiyat / rakip / zamanlama..." /></FormField>
        )}

        <div style={{ display:'flex', gap: 8, justifyContent:'space-between', marginTop: 8 }}>
          {editing && <Button variant="ghost" onClick={del} style={{ color:'var(--danger)' }}>Sil</Button>}
          <div style={{ display:'flex', gap: 8, marginLeft:'auto' }}>
            <Button variant="ghost" onClick={onClose}>İptal</Button>
            <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '...' : 'Kaydet'}</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
